/**
 * Device identity for the admin console.
 *
 * THIS IS NO LONGER HOW ANYBODY GETS INTO THE CONSOLE. A TLS client certificate
 * held in the operating system keychain is, and there is no enrolment screen
 * any more - `generateKeyPair` and `saveIdentity` have no caller in the
 * application. What is still live is the signing half: a browser enrolled
 * before the change keeps its key, and `buildCommand` and `signCommand` are
 * still how a privileged control command is signed.
 *
 * It stays rather than being deleted because the certificate work and the
 * removal of this mechanism are two migrations, and doing both at once to the
 * one path that decides who reaches a payment platform's controls is how a
 * change that was meant to end a lockout causes one. It goes on its own, later,
 * once certificates have been proven in production.
 *
 * WHY IT WAS REPLACED, so nobody reinstates it without knowing: the key was
 * created with `extractable: false` and stored in IndexedDB, which made it
 * genuinely unstealable and completely undurable. Clearing site data in Safari
 * - an ordinary thing for a person to do - destroyed the owner's only admin
 * credential and left no way back except a shell on the server. The keychain
 * survives that.
 *
 * The original reasoning, which still holds for what this does:
 *
 * Spec: "Do NOT copy simple user-supplied DEVICEID semantics as a security
 * mechanism. A browser-generated device ID is NOT trusted authentication ...
 * prefer registered device, device public/private key pair, non-exportable
 * private key where supported."
 *
 * The browser equivalent of a TPM-held key is a WebCrypto key generated with
 * `extractable: false`. The private half then cannot be exported by any
 * script - including this one, including an XSS payload - while still being
 * usable for signing and storable in IndexedDB. The public half goes to the
 * server at enrolment.
 *
 * The Windows Admin executable does the same thing against the Windows
 * certificate store or TPM; only the key container differs.
 */

const DB_NAME = 'wyocspay-admin';
const STORE = 'device';
const RECORD = 'identity';

export interface DeviceIdentity {
  deviceId: string;
  userId: string;
  privateKey: CryptoKey;
  publicKeyPem: string;
}

interface StoredIdentity {
  deviceId: string;
  userId: string;
  privateKey: CryptoKey;
  publicKeyPem: string;
}

export function ed25519Supported(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error as Error);
  });
}

async function readStored(): Promise<StoredIdentity | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(RECORD);
    request.onsuccess = () => resolve((request.result as StoredIdentity | undefined) ?? null);
    request.onerror = () => reject(request.error as Error);
  });
}

async function writeStored(value: StoredIdentity): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(value, RECORD);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error as Error);
  });
}

export async function clearDevice(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(RECORD);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

function toPem(spki: ArrayBuffer): string {
  const b64 = btoa(String.fromCharCode(...new Uint8Array(spki)));
  const lines = b64.match(/.{1,64}/g) ?? [];
  return `-----BEGIN PUBLIC KEY-----\n${lines.join('\n')}\n-----END PUBLIC KEY-----\n`;
}

/**
 * Generate a fresh keypair. `extractable: false` applies to the private key;
 * the public key stays exportable, which is what we need to enrol it.
 */
export async function generateKeyPair(): Promise<{ privateKey: CryptoKey; publicKeyPem: string }> {
  const pair = (await crypto.subtle.generateKey({ name: 'Ed25519' }, false, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;

  if (pair.privateKey.extractable) {
    // Should never happen, but if a browser ignored the flag we would be
    // storing an exportable admin credential. Refuse rather than pretend.
    throw new Error('Този браузър създаде експортируем частен ключ; регистрацията е отказана.');
  }

  const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
  return { privateKey: pair.privateKey, publicKeyPem: toPem(spki) };
}

export async function saveIdentity(identity: StoredIdentity): Promise<void> {
  await writeStored(identity);
}

export async function loadIdentity(): Promise<DeviceIdentity | null> {
  try {
    return await readStored();
  } catch {
    return null;
  }
}

/**
 * What to say when a privileged action is attempted from a browser with no
 * signing key.
 *
 * This is now a reachable state rather than an impossible one. A client
 * certificate admits somebody to the console; it does not sign control
 * commands, which still require a device key held by the browser. So a fresh
 * browser on a certified machine can read everything and change nothing until a
 * key is registered for it, and the message has to say so plainly instead of
 * failing with a null dereference.
 */
export const NO_SIGNING_KEY =
  'Този браузър няма ключ за подписване. Сертификатът дава достъп до администрацията, но привилегированите команди се подписват с ключ на устройството. Регистрирайте този браузър от „Регистрирани устройства“.';

export function requireIdentity(identity: DeviceIdentity | null): DeviceIdentity {
  if (!identity) throw new Error(NO_SIGNING_KEY);
  return identity;
}

export interface ControlCommand {
  commandId: string;
  requestId: string;
  actorId: string;
  deviceId: string;
  timestamp: number;
  nonce: string;
  target: string;
  previousVersion: number;
  transition: string;
  params: Record<string, unknown>;
}

/**
 * Must byte-for-byte match `canonicalCommand` in packages/shared/src/command.ts.
 * Fields are length-prefixed so no value can impersonate a field boundary.
 */
function canonical(cmd: ControlCommand): string {
  const fields: Array<[string, string]> = [
    ['commandId', cmd.commandId],
    ['requestId', cmd.requestId],
    ['actorId', cmd.actorId],
    ['deviceId', cmd.deviceId],
    ['timestamp', String(cmd.timestamp)],
    ['nonce', cmd.nonce],
    ['target', cmd.target],
    ['previousVersion', String(cmd.previousVersion)],
    ['transition', cmd.transition],
    ['params', stable(cmd.params)],
  ];
  return fields
    .map(([k, v]) => `${k}=${new TextEncoder().encode(v).length}:${v}`)
    .join('&');
}

function stable(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
}

export async function signCommand(
  identity: DeviceIdentity,
  cmd: ControlCommand,
): Promise<string> {
  const signature = await crypto.subtle.sign(
    { name: 'Ed25519' },
    identity.privateKey,
    new TextEncoder().encode(canonical(cmd)),
  );
  return btoa(String.fromCharCode(...new Uint8Array(signature)));
}

function randomToken(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return btoa(String.fromCharCode(...buf)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
function randomId(prefix: string): string {
  const buf = new Uint8Array(20);
  crypto.getRandomValues(buf);
  let bits = 0, value = 0, out = '';
  for (const byte of buf) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return `${prefix}_${out}`;
}

/** Build a command with a fresh id and nonce. The nonce is single-use server side. */
export function buildCommand(input: {
  actorId: string;
  deviceId: string;
  target: string;
  transition: string;
  previousVersion: number;
  params?: Record<string, unknown>;
}): ControlCommand {
  return {
    commandId: randomId('cmd'),
    requestId: randomId('req'),
    actorId: input.actorId,
    deviceId: input.deviceId,
    timestamp: Math.floor(Date.now() / 1000),
    nonce: randomToken(16),
    target: input.target,
    previousVersion: input.previousVersion,
    transition: input.transition,
    params: input.params ?? {},
  };
}
